import {
  CommentFragment,
  CommentsDocument,
  CommentsQuery,
  isError,
  useCommentCreatedSubscription,
  useCommentUpdatedSubscription,
  useCommentsQuery,
  withStandardListHook,
} from '@spotify/confidence-graphql';

type UseCommentsVariables = {
  resource?: string | null;
};

const useCommentsImpl = withStandardListHook<
  CommentsQuery,
  CommentFragment,
  'comments',
  UseCommentsVariables
>(useCommentsQuery, 'comments', {
  query: CommentsDocument,
  typename: 'CollaborationV1ListCommentsResponse',
});

export const useComments: typeof useCommentsImpl = options => {
  useCommentCreatedSubscription({
    onData({ data: { data: event }, client }) {
      const comment = event?.commentCreated?.comment;
      if (!comment) return;

      client.cache.updateQuery<CommentsQuery>(
        {
          query: CommentsDocument,
          variables: {
            resource: comment.resource,
            filter: null,
          },
        },
        cachedData => updateCommentsQuery(cachedData, comment),
      );
    },
  });

  // Update the cache when a comment is updated. We depend on the
  // normalized cache to do the business for us.
  useCommentUpdatedSubscription({});

  return useCommentsImpl(options);
};

export function updateCommentsQuery(
  data: CommentsQuery | null,
  comment: CommentFragment,
): CommentsQuery | null | undefined {
  if (!data || isError(data?.comments)) return undefined;

  const comments = data.comments?.comments ?? [];

  // If comment exists in cache already, lets not change anything
  if (comments.find(c => c.name === comment.name)) return undefined;

  return {
    ...data,
    comments: {
      ...data.comments,
      __typename: 'CollaborationV1ListCommentsResponse',
      comments: [comment, ...comments],
    },
  };
}
